/** @file clip.blendPoses: keys a frame with the blend of the poses at two frames. */
import type { NodeKey } from '@zilo/pose3d-format/types/clip';
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { checkTuple } from '@zilo/pose3d-format/validate/fields/checkTuple';
import type { CommandDef } from '../apply/commandDef';
import { blendNodeKey } from '../pose/blendNodeKey';
import { sampleNodeKey } from '../pose/sampleNodeKey';
import { findClip } from '../puppetDoc/findClip';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { keyedNodes } from './keyedNodes';
import { writePose } from './writePose';

/**
 * `clip.blendPoses { clip, from: [a, b], weight, frame, nodes? }` → `{ frame }`: per node `blendNodeKey` of the
 * samples at a and b (`weight` 0 = a, 1 = b), written like `clip.mirrorPose`. Inverse: the previous key.
 */
export const clipBlendPosesCommand: CommandDef<'clip.blendPoses'> = {
  name: 'clip.blendPoses',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['clip', 'from', 'weight', 'frame', 'nodes']);
    return compact({
      clip: r.str('clip'), from: r.check('from', checkTuple(2, { int: true, min: 0 })) as [number, number],
      weight: r.num('weight', { min: 0, max: 1 }), frame: r.int('frame', { min: 0 }),
      nodes: r.opt('nodes', (k) => r.check(k, checkArray(checkString())) as string[]),
    });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const { name, clip } = findClip(draft, input.clip);
    const pose: Record<string, NodeKey> = {};
    for (const n of input.nodes ?? keyedNodes(clip)) {
      const [a, b] = [sampleNodeKey(clip, n, input.from[0]), sampleNodeKey(clip, n, input.from[1])];
      if (Object.keys(a).length + Object.keys(b).length > 0) pose[n] = blendNodeKey(a, b, input.weight);
    }
    return writePose(draft, { clipName: name, frame: input.frame, nodes: pose, merge: input.nodes !== undefined });
  },
};
